/* global PerformanceObserver */
var NpawObject = require('../object')
var RUMSpeedIndex = require('./speedIndex')

/**
 * This static class provides information about the load times of the page
 *
 * @constructs NpawObject
 * @extends npaw.NpawObject
 * @memberof npaw
 *
 */
var BrowserLoadTimes = NpawObject.extend({
  constructor: function (appAnalytics) {
    this.appAnalytics = appAnalytics;
    this.timeObject = undefined;
    this.playerSetup = undefined;
    this.myTimesObject = {};
    this.perfObject = undefined;

    // `loadTimes` is bound to the browser-level page load. In a SPA there is
    // exactly ONE page load per plugin instance, so the event must be emitted
    // exactly once per instance — never per AppAnalytics session. Sessions
    // born later in the page lifetime (e.g. login after an anonymous session)
    // share the same underlying `performance.timing` values, so re-emitting
    // would just duplicate the bootstrap data into sessions that never
    // experienced a page load.
    //
    // The two assists added here are purely about making sure the single
    // emission actually happens, even when the plugin / first session arrive
    // late:
    //   - `_loadTimesSent` is the single-shot dedup flag.
    //   - The `onSessionStart` hook is a retry trigger for the case where the
    //     session was not yet started at `window.load + 1s`.
    //   - The `document.readyState === 'complete'` branch handles plugins
    //     instantiated *after* `window.load` already fired (async bundle,
    //     lazy init), where `addEventListener('load', ...)` would never
    //     trigger its callback on its own.
    this._timingsReady = false;
    this._cachedValues = null;
    this._loadTimesSent = false;
    this._windowLoadHandled = false;
    this._sessionStartHooked = false;

    if (typeof window !== 'undefined') {
      if (window.performance && window.performance.timing) {
        try {
          if (typeof window.performance.getEntriesByType === 'function') {
            this.perfObject = window.performance;
          }
        } catch (err) {
          // Nothing
        }
        this.timeObject = window.performance.timing;
      }

      this._scheduleWindowLoaded();
    }
    try {
      if (typeof PerformanceObserver === 'function') {
        const observer = new PerformanceObserver((list, obj) => {
          const entries = list.getEntries();
          this.myTimesObject.largestContentfulPaint = entries[entries.length - 1].renderTime;
        });
        if (PerformanceObserver.supportedEntryTypes.indexOf('largest-contentful-paint') > -1) {
          observer.observe({ entryTypes: ['largest-contentful-paint'] });
        }
      }
    } catch (err) {
      // Cant use PerformanceObserver, entryTypes...
    }

    this._hookOnSessionStart();
  },

  /**
   * Wire up the page-load entry point.
   *
   * If the document is already fully loaded when the plugin is instantiated
   * (e.g. plugin script appended asynchronously after `window.load`),
   * `addEventListener('load', ...)` would never fire its callback because
   * the event has already passed. In that case we trigger `_windowLoaded`
   * directly on the next tick.
   *
   * Otherwise we keep the original behaviour: attach a single listener to
   * `window.load`. The listener is registered at most once per instance.
   */
  _scheduleWindowLoaded: function () {
    var alreadyLoaded = (typeof document !== 'undefined') && (document.readyState === 'complete');
    if (alreadyLoaded) {
      setTimeout(this._windowLoaded.bind(this), 0);
    } else if (typeof window.addEventListener === 'function') {
      window.addEventListener('load', this._windowLoaded.bind(this));
    }
  },

  _windowLoaded: function () {
    // Defensive idempotency: in some races the event listener may be invoked
    // while the late-init `setTimeout` is also queued. We collect timings
    // exactly once per instance regardless.
    if (this._windowLoadHandled) return;
    this._windowLoadHandled = true;

    this.myTimesObject.onLoad = new Date().getTime();
    this._getEnoughFPS();
    setTimeout(this._fireLoadTimesEvent.bind(this), 1000);
  },

  _fireLoadTimesEvent: function () {
    this._getLastMetrics();
    if (!this._timingsReady) {
      this._cachedValues = this._getAllValues();
      this._timingsReady = true;
    }
    this._tryFireLoadTimes();
  },

  /**
   * Register a single listener on AppAnalytics session start so the
   * `loadTimes` event can still be emitted if the first session was not yet
   * started at `window.load + 1s` (typical of Smart TV / late-init flows).
   *
   * The hook is installed at most once. Combined with the single-shot
   * `_loadTimesSent` flag in `_tryFireLoadTimes`, this guarantees AT MOST
   * one `loadTimes` event per plugin instance — even if AppAnalytics opens
   * multiple sessions during the same page load (anonymous → login, session
   * expiration recovery, etc.). Subsequent sessions intentionally do NOT
   * receive `loadTimes` because they did not experience a page load of
   * their own; their `performance.timing` values would be identical to the
   * first session's and would only inflate the dataset.
   *
   * If AppAnalytics does not expose `onSessionStart` (older builds), the
   * registration is silently skipped and the original single-shot behaviour
   * is preserved.
   */
  _hookOnSessionStart: function () {
    if (this._sessionStartHooked) return;
    try {
      if (this.appAnalytics && typeof this.appAnalytics.onSessionStart === 'function') {
        this.appAnalytics.onSessionStart(this._tryFireLoadTimes.bind(this));
        this._sessionStartHooked = true;
      }
    } catch (err) {
      // Defensive: never let a listener wiring problem break the plugin.
    }
  },

  /**
   * Emit the `loadTimes` event AT MOST ONCE per plugin instance.
   *
   * The call is gated by:
   *   1. `_timingsReady` — page load timings have already been collected.
   *   2. `appAnalytics.isActive() && isStarted()` — an AppAnalytics session
   *      exists and has been started; otherwise the event has no session to
   *      attach to.
   *   3. `_loadTimesSent === false` — the event has not been emitted yet.
   *
   * This method is the single emission path and is intentionally idempotent:
   * it can be invoked any number of times from any of the two entry points
   * (`_fireLoadTimesEvent` at `window.load + 1s` and `onSessionStart`) and
   * will produce at most one `session/event?name=loadTimes` request.
   */
  _tryFireLoadTimes: function () {
    if (this._loadTimesSent) return;
    if (!this._timingsReady || !this._cachedValues) return;
    if (!this.appAnalytics) return;
    if (!this.appAnalytics.isActive() || !this.appAnalytics.isStarted()) return;

    this.appAnalytics.fireEvent('loadTimes', {}, this._cachedValues);
    this._loadTimesSent = true;
  },

  _getAllValues: function () {
    const ret = {
      // Time between the navigation starts and the page loadEventEnd
      PageLoadTime: this.getPageLoadTime(),
      // Domain lookup time
      DNSTime: this.getDnsTime(),
      // Connection time
      TCPTime: this.getTcpTime(),
      // Handshake (https) connection time
      HandshakeTime: this.getHandshakeTime(),
      // Time between navigation starts and DOM is ready
      DomReadyTime: this.getDomReadyTime(),
      // Time between navigation start and beginning of the backend response
      BackendTime: this.getBackendTime(),
      // Time between navigation starts and page load event
      FrontendTime: this.getFrontendTime(),
      // The maximum of First Paint / First Contentful Paint and domContentLoadedEventEnd
      VisualReady: this.getTimeToVisuallyReady(),
      // https://developer.mozilla.org/en-US/docs/Glossary/Time_to_interactive
      TimeToInteractive: this.getTimeToInteractive(),
      // Accumulated time to download all the JS files
      JsTime: this.getJSTime(),
      // Accumulated time to download all the CSS files
      CssTime: this.getCSSTime(),
      // Accumulated time to download all the images
      ImageTime: this.getImageTime(),
      // Accumulated time to download all the fonts
      FontTime: this.getFontTime(),
      // Average latency of all the file requests
      AvgReqLatency: this.getAvgReqLatency(),
      // Highest latency of all the file requests
      MaxReqLatency: this.getMaxReqLatency(),
      // https://developer.mozilla.org/en-US/docs/Glossary/First_paint
      FirstPaint: this.getFirstPaint(),
      // https://developer.mozilla.org/en-US/docs/Glossary/First_contentful_paint
      FirstContentfulPaint: this.getFirstContentfulPaint(),
      // https://rockcontent.com/blog/largest-contentful-paint/
      LargestContentfulPaint: this.getLargestContentfulPaint(),
      // https://developer.mozilla.org/en-US/docs/Glossary/Speed_index
      SpeedIndex: this.getSpeedIndex()
    };
    for (const key in ret) {
      if (ret[key] === null || ret[key] === undefined || ret[key] < 0) {
        delete ret[key];
      } else {
        ret[key] = Math.round(ret[key]);
      }
    }
    return ret;
  },

  // Getters

  getPageLoadTime: function () {
    let ret;
    if (this.timeObject) {
      ret = this.timeObject.loadEventEnd - this.timeObject.navigationStart;
    }
    return ret;
  },

  getPlayerStartupTime: function () {
    let ret;
    if (this.timeObject && this.playerSetup) {
      ret = this.playerSetup - this.timeObject.navigationStart;
    }
    return ret;
  },

  getDnsTime: function () {
    let ret;
    if (this.timeObject) {
      ret = this.timeObject.domainLookupEnd - this.timeObject.domainLookupStart;
    }
    return ret;
  },

  getTcpTime: function () {
    let ret;
    if (this.timeObject) {
      ret = this.timeObject.connectEnd - this.timeObject.connectStart;
    }
    return ret;
  },

  getHandshakeTime: function () {
    let ret;
    if (this.timeObject && this.timeObject.secureConnectionStart) {
      ret = this.timeObject.connectEnd - this.timeObject.secureConnectionStart;
    }
    return ret;
  },

  getDomReadyTime: function () {
    let ret;
    if (this.timeObject) {
      ret = this.timeObject.domComplete - this.timeObject.navigationStart;
    }
    return ret;
  },

  getBackendTime: function () {
    let ret;
    if (this.timeObject) {
      ret = this.timeObject.responseStart - this.timeObject.navigationStart;
    }
    return ret;
  },

  getFrontendTime: function () {
    let ret;
    if (this.timeObject) {
      ret = this.myTimesObject.onLoad - this.timeObject.responseStart;
    }
    return ret;
  },

  getTimeToVisuallyReady: function () {
    const ret = this.myTimesObject.firstPaint || 0;
    if (this.timeObject) {
      return Math.max(
        ret,
        this.timeObject.domContentLoadedEventEnd - this.timeObject.navigationStart || 0
      // this.myTimesObject.heroImages - this.timeObject.navigationStart || 0
      );
    }
    return ret || undefined;
  },

  getTimeToInteractive: function () {
    if (this.myTimesObject.fps && this.getTimeToVisuallyReady()) {
      return Math.max(this.myTimesObject.fps, this.getTimeToVisuallyReady());
    } else {
      setTimeout(() => {
        this.getTimeToInteractive();
      }, 500);
    }
  },

  getJSTime: function () {
    return this._getXTime('script');
  },

  getCSSTime: function () {
    return this._getXTime('css');
  },

  getImageTime: function () {
    return this._getXTime('img');
  },

  getFontTime: function () {
    return this._getXTime('css', ['.woff', '.otf', '.ttf']);
  },

  getAvgReqLatency: function () {
    try {
      if (this.perfObject && typeof this.perfObject.getEntriesByType === 'function') {
        let count = 0;
        let latency = 0;
        const scripts = this.perfObject.getEntriesByType('resource');
        for (var i in scripts) {
          if (scripts[i].requestStart && scripts[i].responseStart) {
            latency += scripts[i].responseStart - scripts[i].requestStart;
          }
          count++;
        }
        return latency / count;
      }
    } catch (err) {
      // Nothing
    }
    return undefined;
  },

  getFirstPaint: function () {
    return this.myTimesObject ? this.myTimesObject.firstPaint : undefined;
  },

  getFirstContentfulPaint: function () {
    return this.myTimesObject ? this.myTimesObject.firstContentfulPaint : undefined;
  },

  getLargestContentfulPaint: function () {
    return this.myTimesObject ? this.myTimesObject.largestContentfulPaint : undefined;
  },

  getMaxReqLatency: function () {
    try {
      if (this.perfObject && typeof this.perfObject.getEntriesByType === 'function') {
        const scripts = this.perfObject.getEntriesByType('resource');
        let latency = 0;
        for (const i in scripts) {
          if (scripts[i].requestStart && scripts[i].responseStart) {
            latency = Math.max(latency, scripts[i].responseStart - scripts[i].requestStart);
          }
        }
        return latency;
      }
    } catch (err) {
      // Nothing
    }
    return undefined;
  },

  getSpeedIndex: function () {
    let ret;
    if (typeof window !== 'undefined' && window.performance && typeof window.performance.getEntriesByType === 'function') {
      try {
        ret = RUMSpeedIndex();
      } catch (e) {
        // nothing
      }
    }
    return ret;
  },

  _getXTime: function (type, validExtensions) {
    let ret = 0;
    try {
      if (this.perfObject && typeof this.perfObject.getEntriesByType === 'function') {
        const scripts = this.perfObject.getEntriesByType('resource');
        for (const i in scripts) {
          if (scripts[i].initiatorType === type) {
            if (!validExtensions) {
              ret += scripts[i].duration;
            } else {
              let valid = false;
              for (var ext in validExtensions) {
                if (scripts[i].name.indexOf(validExtensions[ext] > 0)) {
                  valid = true;
                }
              }
              if (valid) {
                ret += scripts[i].duration;
              }
            }
          }
        }
      }
    } catch (err) {
      // Nothing
    }
    return Math.round(ret) || undefined;
  },

  _getLastMetrics: function () {
    let firstPaint = undefined;
    let contentfulPaint = undefined;
    try {
      if (this.perfObject && typeof this.perfObject.getEntriesByType === 'function') {
        const entries = this.perfObject.getEntriesByType('paint');
        for (const i in entries) {
          if (entries[i].name === 'first-paint') {
            firstPaint = entries[i].startTime;
          } else if (entries[i].name === 'first-contentful-paint') {
            contentfulPaint = entries[i].startTime;
          }
        }
      }
    } catch (err) {
      // Nothing
    }
    // First paint
    if (!firstPaint && this.timeObject) {
      firstPaint = this.timeObject.msFirstPaint - this.timeObject.navigationStart;
    }
    // TODO first paint for firefox
    this.myTimesObject.firstPaint = firstPaint;
    // First contentful paint
    this.myTimesObject.firstContentfulPaint = contentfulPaint;
    // Others
    this.getTimeToInteractive();
  },

  _getEnoughFPS: function () {
    if (this.timeObject && typeof window !== 'undefined') {
      const req = window.requestAnimationFrame || window.mozRequestAnimationFrame || window.webkitRequestAnimationFrame || window.msRequestAnimationFrame || function () {};
      this.preFPS = new Date().getTime();
      req(() => {
        const now = new Date().getTime();
        if (now < this.preFPS + 50) {
          this.myTimesObject.fps = now - this.timeObject.navigationStart;
        } else {
          setTimeout(() => {
            return this._getEnoughFPS();
          }, 50);
        }
      });
    } else {
      return true;
    }
  },

  // Setters

  setPlayerSetupTime: function () {
    this.playerSetup = this.playerSetup || new Date().getTime();
  }
});

module.exports = BrowserLoadTimes;
